<template>
  <FormOverlaysBlock>
    <!-- Top actions row: top/left on left side, special on right side -->
    <div class="form-top-actions">
      <div class="actions-left">
        <FormActionsRow :position="['top', 'left']" />
      </div>
      <div class="actions-right">
        <FormActionsRow :position="['special']" />
      </div>
    </div>

    <FormErrorBanner />

    <FormSection v-if="header.length" section-role="header" :data="header" />

    <div v-if="main.length" class="form-main-body">
      <MultipleMain :main-tabs="main" :cols="multipleMainWidth" :orientation="mainLayout" />
    </div>

    <FormSection v-if="footer.length" section-role="footer" :data="footer" />

    <div class="row form-actions-bottom">
      <div class="col-12">
        <FormActionsRow :position="['bottom', 'right']" />
      </div>
    </div>
  </FormOverlaysBlock>
</template>

<script setup lang="ts">
import { useListeners } from '@/common/composables/useListeners';
import { usePageData } from '@/common/composables/usePageData';
import { useFormStates, useFormTimerEverySecondTick } from '@/common/composables';
import { FormProviderKey, RegisterFormKey, UnRegisterFormKey } from '@/components/platform/injection-keys';
import { computed, inject, nextTick, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue';
import { clearDeferredFormVisibilityForActionIds } from '@/common/utils/deferredFormVisibility';
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId';
import { startWorkflowMeasure } from '@/common/utils/performanceTrace';
import {
  buildRootOnLoadDedupKey,
  buildRootOnLoadRunTrigger,
  combineRootOnLoadScript,
  isRootFormOnLoadReady,
  readRootFormFunctions,
  readRootFormOnLoadScript,
  shouldRunRootFormOnLoadOnClientBeforeBuild,
  extractScriptRegistryElementNames,
  extractScriptRegistryActionIds,
    shouldRunRootFormOnLoadForPage,
    waitForRegistryElements,
    waitForRegistryActions,
    reserveRootOnLoadGlobally,
    isRootOnLoadGloballyReserved,
    releaseRootOnLoadGlobally,
} from '@/common/utils/formRootOnLoad';
import { filterStatesForPageLayout } from '@/common/utils/filterStatesForPageLayout';
import {
  clearPreservedPageTabMarker,
  reapplyPreservedPageTabs,
} from '@/common/utils/preservePageTabState';
import {
  reportFormElementNameConflicts,
  validateFormElementNames,
} from '@/common/utils/validateFormElementNames';
import { formHasRenderableLayout } from '@/services/parse/parse.service';
import { applyOpenSuggesterFromStates } from '@/common/utils/applyOpenSuggesterFromStates';
import { logAttachmentStoreDebug } from '@/common/utils/attachmentDebugLog';
import { clearFormSoftMessages } from '@/services/form-events/formMessagesStore';
import {
  applyFormErrorFromPayload,
  clearFormError,
} from '@/common/utils/formErrorStore'
import FormOverlaysBlock from '@/components/platform/form/FormOverlaysBlock.vue'
import FormErrorBanner from '@/components/platform/form/FormErrorBanner.vue'
import FormActionsRow from './FormActionRow.vue';
import MultipleMain from './MultipleMain.vue';
import FormSection from './Section.vue';

const { pageData, pageDataSlot, pageRegistryFormId, pageFields, pageStates } = usePageData();
const { invokeListeners, pageListeners } = useListeners();
const { processStates } = useFormStates();
const lastProcessedStatesKey = ref('');

const registerForm = inject(RegisterFormKey)
const unRegisterForm = inject(UnRegisterFormKey)
const formProvider = inject(FormProviderKey, null)

const pagesDataSlotId = computed(() => {
  return String(pageDataSlot.value ?? '').trim()
})

/** Разметка может приходить на корне form или внутри form.form */
const formObj = computed<any>(() => {
  const f = (pageData.value as any)?.form
  if (!f || typeof f !== 'object') {
    return {}
  }
  const topHasLayout =
    (Array.isArray(f.main) && f.main.length > 0) ||
    (Array.isArray(f.header) && f.header.length > 0) ||
    (Array.isArray(f.footer) && f.footer.length > 0)
  if (topHasLayout) {
    return f
  }
  const inner = f.form
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const innerHasLayout =
      (Array.isArray(inner.main) && inner.main.length > 0) ||
      (Array.isArray(inner.header) && inner.header.length > 0) ||
      (Array.isArray(inner.footer) && inner.footer.length > 0) ||
      inner.panels != null
    if (innerHasLayout) {
      return { ...f, ...inner }
    }
  }
  return f
})
const header = computed(() => formObj.value?.header ?? []);
const footer = computed(() => formObj.value?.footer ?? []);
const main = computed(() => formObj.value?.main ?? []);
const formId = computed(() => {
  if (pageRegistryFormId.value) return pageRegistryFormId.value
  const pd = pageData.value as any
  return resolveElementRegistryFormId(pd, pagesDataSlotId.value)
})

// Получаем formId для вызова listeners
const numericFormId = computed(() => {
  const formData = pageData.value as any
  // Пытаемся найти ID формы в данных - @TODO: уточнить может быть? в разных местах
  const formIdFromData =
    formData?.form?.form?._attributes?.id ||
    formData?.form?._attributes?.id ||
    formData?.formId ||
    formId.value

  if (!formIdFromData) return null

  // Если это уже число, возвращаем его
  if (typeof formIdFromData === 'number') {
    return formIdFromData
  }
  const parsed = parseInt(String(formIdFromData), 10)
  return isNaN(parsed) ? null : parsed
})

const buildStatesKey = (slotId: unknown, states: unknown, registryFormId: unknown) => {
  if (!Array.isArray(states) || states.length === 0) {
    return ''
  }
  return JSON.stringify({
    slotId: String(slotId ?? ''),
    registryFormId: String(registryFormId ?? ''),
    states: states.map((state: any) => ({
      id: state?.element_id,
      level: state?.element_level,
      name: state?.element_name,
      visible: state?.is_visible,
      editable: state?.is_editable,
      enabled: state?.is_enabled,
      refill: state?.is_refill,
      focused: state?.is_focused,
      empty: state?.is_empty,
    })),
  })
}

const processFormStatesOnce = async (
  slotId: unknown,
  states?: unknown,
  registryFormIdSnapshot?: string | number | null,
) => {
  const sid = String(slotId ?? '').trim()
  const stateList = Array.isArray(states) ? states : pageStates.value
  const registryFormId = String(registryFormIdSnapshot ?? formId.value ?? '')
  const formRoot = (pageData.value as any)?.form
  const scopedStates = filterStatesForPageLayout(stateList as unknown[], formRoot) as typeof stateList
  const key = buildStatesKey(sid, scopedStates, registryFormId)
  if (!sid || !key || key === lastProcessedStatesKey.value) {
    return
  }

  const attachmentStates = (scopedStates as any[]).filter((state) => {
    return String(state?.element_type ?? '').trim().toUpperCase() === 'FILEATTACHMENTS'
  }).map((state) => ({
    name: state?.element_name ?? null,
    id: state?.element_id ?? null,
    type: state?.element_type ?? null,
    refill: state?.is_refill ?? null,
    empty: state?.is_empty ?? null,
  }))
  if (attachmentStates.length > 0) {
    logAttachmentStoreDebug('form-process-states-once', {
      slotId: sid,
      registryFormId,
      stateCount: scopedStates.length,
      attachmentStates,
    })
  }

  lastProcessedStatesKey.value = key
  await nextTick()

  if (formProvider?.openSuggesterElement) {
    applyOpenSuggesterFromStates(
      scopedStates as any[],
      formProvider.openSuggesterElement.bind(formProvider),
      registryFormId,
    )
  }

  const endMeasure = startWorkflowMeasure('Form.processFormStatesOnce', {
    slotId: sid,
    registryFormId,
    stateCount: scopedStates.length,
  })
  try {
    await processStates(sid, scopedStates, undefined, registryFormId)
  } catch (error) {
    console.error('[Form.processFormStatesOnce]', sid, registryFormId, error)
  } finally {
    endMeasure()
  }
}

const lastRootOnLoadKey = ref('')
const rootOnLoadInFlightKey = ref('')
const formMounted = ref(false)
let rootOnLoadSchedulePending = false

const rootOnLoadEvent = computed(() => {
  const fields = (pageFields.value ?? {}) as Record<string, any>
  return fields._event?.value ?? fields._event
})

const shouldRunRootFormOnLoad = computed(() =>
  shouldRunRootFormOnLoadForPage({
    event: rootOnLoadEvent.value,
    pageData: pageData.value,
  }),
)

const rootOnLoadDedupKey = computed(() => {
  if (!shouldRunRootFormOnLoad.value || !pagesDataSlotId.value || !formId.value) {
    return ''
  }
  const fields = (pageFields.value ?? {}) as Record<string, any>
  return buildRootOnLoadDedupKey({
    slotId: pagesDataSlotId.value,
    registryFormId: formId.value,
    event: rootOnLoadEvent.value,
    preOpenId: fields.preOpenId?.value ?? fields.preOpenId,
    activateId: fields._id?.value ?? fields._id,
    sessionId: (pageData.value as Record<string, unknown> | undefined)?.sessionId,
    rootOnLoadNonce: (pageData.value as Record<string, unknown> | undefined)?.$rootOnLoadNonce,
  })
})

const rootOnLoadRunTrigger = computed(() => {
  const form = (pageData.value as any)?.form
  return buildRootOnLoadRunTrigger(
    rootOnLoadDedupKey.value,
    readRootFormOnLoadScript(form),
    readRootFormFunctions(form),
  )
})

const runRootFormOnLoadOnce = async () => {
  const form = (pageData.value as any)?.form
  const wasPreBuilt = (pageData.value as any)?.$preBuiltRootOnLoad === true
  const preBuiltStates = Array.isArray((pageData.value as any)?.jsResult?.states)
    ? (pageData.value as any).jsResult.states
    : []
  if (
    wasPreBuilt &&
    preBuiltStates.length > 0 &&
    shouldRunRootFormOnLoadOnClientBeforeBuild(form)
  ) {
    clearPreservedPageTabMarker(pageData.value)
    return
  }
  const script = readRootFormOnLoadScript(form)
  const functions = readRootFormFunctions(form)
  const slotId = pagesDataSlotId.value
  const registryFormId = formId.value
  const runKey = rootOnLoadRunTrigger.value
  if (!script || !shouldRunRootFormOnLoad.value || !slotId || !registryFormId || !runKey) {
    return
  }

  if (
    !isRootFormOnLoadReady({
      script,
      functions,
    })
  ) {
    return
  }

  if (
    lastRootOnLoadKey.value === runKey ||
    rootOnLoadInFlightKey.value === runKey ||
    isRootOnLoadGloballyReserved(runKey)
  ) {
    return
  }
  rootOnLoadInFlightKey.value = runKey
  lastRootOnLoadKey.value = runKey
  reserveRootOnLoadGlobally(runKey)

  try {
    await nextTick()
    const combinedOnLoadScript = combineRootOnLoadScript(functions, script)
    const registryElementNames = extractScriptRegistryElementNames(combinedOnLoadScript)
    const registryActionIds = extractScriptRegistryActionIds(combinedOnLoadScript)
    if (registryElementNames.length && formProvider?.getElements) {
      await waitForRegistryElements(
        () => formProvider.getElements?.() ?? {},
        registryElementNames,
        { timeoutMs: 3000, budgetMs: 100 },
      )
    }
    if (registryActionIds.length && formProvider?.getActions) {
      await waitForRegistryActions(
        () => formProvider.getActions?.() ?? {},
        registryActionIds,
        { timeoutMs: 3000, budgetMs: 100 },
      )
    }
    if (!formProvider?.runEvent) {
      releaseRootOnLoadGlobally(runKey)
      return
    }
    // Снимок слота (возврат из дочернего окна) дописывает таблицы после reload.
    // onLoad читает их размер для setVisible, поэтому снимок нужен до скрипта.
    formProvider.resyncTabSlotFields?.(String(slotId))
    await formProvider.runEvent(combinedOnLoadScript, {
      formId: registryFormId,
      pageSlotId: slotId,
      eventName: 'onLoad',
    })
    formProvider.resyncTabSlotFields?.(String(slotId))
  } finally {
    reapplyPreservedPageTabs(pageData.value)
    if (rootOnLoadInFlightKey.value === runKey) {
      rootOnLoadInFlightKey.value = ''
    }
  }
}

const scheduleRootFormOnLoadOnce = () => {
  const trigger = rootOnLoadRunTrigger.value
  if (!trigger || lastRootOnLoadKey.value === trigger || rootOnLoadInFlightKey.value === trigger) {
    return
  }
  if (rootOnLoadSchedulePending) {
    return
  }
  rootOnLoadSchedulePending = true
  setTimeout(() => {
    rootOnLoadSchedulePending = false
    void runRootFormOnLoadOnce()
  }, 0)
}

watch(
  rootOnLoadRunTrigger,
  (next, prev) => {
    if (!formMounted.value || !next || next === prev) {
      return
    }
    scheduleRootFormOnLoadOnce()
  },
  { flush: 'post' },
)

const multipleMainWidth = computed(() => {
  const mains = formObj.value?.main ?? [];
  return mains.map((m: any) => {
    const raw = m?._attributes?.width;
    if (raw == null || raw === '') return 100;
    const n = parseFloat(String(raw).replace(/%/g, '').trim());
    return Number.isFinite(n) ? n : 100;
  });
});
const mainLayout = computed(() => formObj.value?.mainLayout?._text ?? 'horizontal');

useFormTimerEverySecondTick(pageListeners, invokeListeners)

const runFormMetadataNameValidation = () => {
  const form = formObj.value
  if (!formHasRenderableLayout(form)) return
  const conflicts = validateFormElementNames(form)
  if (!conflicts.length) return
  reportFormElementNameConflicts(conflicts, {
    formId: formId.value || pagesDataSlotId.value || numericFormId.value,
  })
}

watch(formObj, () => runFormMetadataNameValidation(), { immediate: true })

// Ensure form is registered before panels/elements mount
onBeforeMount(() => {
  const id = formId.value as unknown as string
  if (registerForm && id) {
    registerForm(id)
  }
})

// In case formId becomes available a bit later
watch(formId, (id) => {
  if (registerForm && id) {
    registerForm(id as unknown as string)
  }
}, { immediate: false })

onMounted(async () => {
  const endMountedMeasure = startWorkflowMeasure('Form.onMounted', {
    slotId: pagesDataSlotId.value,
    registryFormId: formId.value,
    numericFormId: numericFormId.value,
  })
  formMounted.value = true
  try {
    // TODO: mn.rumynin - скорректировать по ElementRenderer
    // Вызываем listeners для события mount формы
    if (numericFormId.value) {
      const fid = numericFormId.value
      startWorkflowMeasure('Form.onMounted.scheduleMountListener', {
        formId: fid,
      })()
      setTimeout(() => {
        void (async () => {
          const endListenersMeasure = startWorkflowMeasure('Form.mountListener', {
            formId: fid,
          })
          try {
            await invokeListeners('form', fid, 'mount')
          } catch (error) {
            console.error('[Form.mountListener]', fid, error)
          } finally {
            endListenersMeasure()
          }
        })()
      }, 0)
    }

    // Сначала states, затем onLoad — doScenarioAction/reload не затираются is_empty из activate.
    if (pagesDataSlotId.value) {
      const slotId = pagesDataSlotId.value
      const registryFormId = formId.value
      startWorkflowMeasure('Form.onMounted.scheduleProcessStates', {
        slotId,
        registryFormId,
        stateCount: Array.isArray(pageStates.value) ? pageStates.value.length : 0,
      })()
      setTimeout(() => {
        void (async () => {
          try {
            await processFormStatesOnce(slotId, undefined, registryFormId)
          } finally {
            scheduleRootFormOnLoadOnce()
          }
        })()
      }, 0)
    } else {
      scheduleRootFormOnLoadOnce()
    }
  } finally {
    endMountedMeasure()
  }
})

watch(
  () => pageData.value,
  (pd) => {
    const slotId = pagesDataSlotId.value
    if (!slotId || !pd) return
    applyFormErrorFromPayload(slotId, pd)
  },
  { immediate: true },
)

onUnmounted(async () => {
  const id = formId.value as unknown as string
  const slotId = pagesDataSlotId.value
  if (slotId) {
    clearFormSoftMessages(slotId)
    clearFormError(slotId)
  }

  // Очистка deferred action visibility для всех actions формы
  const formData = pageData.value as any
  const actionsArray = formData?.form?.actions?.action
  if (actionsArray) {
    const actionIds: Array<string | number> = []
    const actions = Array.isArray(actionsArray) ? actionsArray : [actionsArray]
    for (const action of actions) {
      const actionId = action?._attributes?.id
      if (actionId != null) {
        actionIds.push(actionId)
      }
    }
    if (actionIds.length > 0) {
      clearDeferredFormVisibilityForActionIds(actionIds)
    }
  }

  // TODO: mn.rumynin - скорректировать по ElementRenderer
  // Вызываем listeners для события unmount формы
  if (numericFormId.value) {
    await invokeListeners('form', numericFormId.value, 'unmount')
  }

  if (unRegisterForm && id) {
    unRegisterForm(id)
  }

  const slot = String(slotId ?? '').trim()
  if (unRegisterForm && slot && slot !== String(id ?? '').trim()) {
    unRegisterForm(slot)
  }
})
</script>

<style scoped lang="scss">
.form-top-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  .actions-left,
  .actions-right {
    display: flex;
    align-items: center;
  }

  //.actions-left {
  //  flex: 0 0 auto;
  //}
  //
  //.actions-right {
  //  flex: 1 1 auto;
  //  justify-content: flex-end;
  //}
}

.form-actions-bottom {
  margin-top: 0.5rem;

  :deep(.element-actions) {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
}
</style>
